import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

/**
 * StatCard — shared admin-console KPI tile.
 *
 * Extracted from `Admin/Overview.tsx` (OKS-15) so both the Overview
 * page and the new Platform Analytics page (OKS-66 `Admin/Analytics.tsx`)
 * consume the same component. Eliminates copy-paste before it can drift.
 *
 * Shape per UX Spec (OKS-15 + OKS-66 §2):
 *   - top row: muted-fg label (CardTitle, text-sm) + Icon (size-4)
 *   - body  : large value (text-3xl, tabular-nums)
 *   - opt.  : caption below the value (text-xs, muted-fg) — used by
 *             OKS-66 Active brands tile for "in last {window}"
 *
 * Values are formatted via `Number.toLocaleString()` so locale-aware
 * thousands separators land for free. `caption` is OPTIONAL and only
 * renders when truthy.
 */
export interface StatCardProps {
  testId: string
  icon: React.ComponentType<{ className?: string; "aria-hidden"?: "true" }>
  label: string
  value: number
  caption?: string
}

export function StatCard({
  testId,
  icon: Icon,
  label,
  value,
  caption,
}: StatCardProps) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
        <CardTitle className="text-muted-foreground text-sm font-medium">
          {label}
        </CardTitle>
        <Icon className="text-muted-foreground size-4" aria-hidden="true" />
      </CardHeader>
      <CardContent>
        <div className="text-foreground text-3xl font-semibold tabular-nums">
          {value.toLocaleString()}
        </div>
        {caption && (
          <p className="text-muted-foreground mt-1 text-xs">{caption}</p>
        )}
      </CardContent>
    </Card>
  )
}
